import { useState } from "react";
import {
  Twitter,
  Facebook,
  Linkedin,
  MessageCircle,
  Link as LinkIcon,
  Check,
} from "lucide-react";

/**
 * Compact share row for any item that has an OG endpoint on the backend.
 *
 * Props:
 *  - shareUrl: full backend OG URL (https://.../api/og/...) — what social bots scrape
 *  - canonicalUrl: clean public URL — what users see when they Copy Link
 *  - text: title/blurb shared alongside the URL
 *  - testidPrefix: prefix for data-testid attributes (default: "share")
 *  - size: "sm" (default) or "md"
 */
export default function ShareButtons({
  shareUrl,
  canonicalUrl,
  text,
  testidPrefix = "share",
  size = "sm",
}) {
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent;
  const dim = size === "md" ? "w-9 h-9" : "w-8 h-8";
  const icon = size === "md" ? 15 : 13;

  // Brand tagline appended to every share; the page-supplied `text` is the
  // title of the specific content being shared, shown first.
  const TAGLINE =
    "Nina Capone\nArtist • Media Personality • Entrepreneur\nBuilding Legacy Through Music, Media & Business.";
  const shareText = text ? `${text}\n\n${TAGLINE}` : TAGLINE;

  const links = [
    {
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`,
      Icon: Twitter,
      testid: `${testidPrefix}-twitter`,
    },
    {
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(shareText)}`,
      Icon: Facebook,
      testid: `${testidPrefix}-facebook`,
    },
    {
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
      Icon: Linkedin,
      testid: `${testidPrefix}-linkedin`,
    },
    {
      label: "Share on WhatsApp",
      href: `https://api.whatsapp.com/send?text=${enc(shareText + "\n" + shareUrl)}`,
      Icon: MessageCircle,
      testid: `${testidPrefix}-whatsapp`,
    },
  ];

  const copy = async () => {
    // Always copy the OG-friendly URL so the link produces a rich preview
    // on Facebook, iMessage, WhatsApp, X, LinkedIn, etc. (those crawlers
    // don't run JS so the canonical SPA URL shows no thumbnail). The OG
    // endpoint auto-redirects real browsers to the canonical article.
    const target = shareUrl || canonicalUrl;
    try {
      await navigator.clipboard.writeText(target);
    } catch {
      const el = document.createElement("textarea");
      el.value = target;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`${testidPrefix}-row`}>
      {links.map(({ label, href, Icon, testid }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          data-testid={testid}
          className={`inline-flex items-center justify-center ${dim} rounded-full border border-white/10 text-white/65 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]/50 transition-all duration-200`}
          onClick={(e) => e.stopPropagation()}
        >
          <Icon size={icon} strokeWidth={1.5} />
        </a>
      ))}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          copy();
        }}
        aria-label="Copy link"
        title="Copy link"
        data-testid={`${testidPrefix}-copy`}
        className={`inline-flex items-center justify-center ${dim} rounded-full border transition-all duration-200 ${
          copied
            ? "border-[color:var(--gold)]/60 text-[color:var(--gold)]"
            : "border-white/10 text-white/65 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]/50"
        }`}
      >
        {copied ? <Check size={icon} /> : <LinkIcon size={icon} />}
      </button>
    </div>
  );
}
